// Formatting of numbers, dates and amounts (Polish notation). Functions return text; the Money/Signed/Pct components add markup.

const nfCache: Record<number, Intl.NumberFormat> = {};
const nf = (d: number) => (nfCache[d] ||= new Intl.NumberFormat("pl-PL", { minimumFractionDigits: d, maximumFractionDigits: d }));

const bad = (v: number | null | undefined): v is null | undefined => v === null || v === undefined || isNaN(v);

/** Number with a thousands separator (plain space, as in the previous version) and `d` decimal places; missing value → "–". */
export const fmtNum = (v: number | null | undefined, d = 0): string =>
  bad(v) ? "–" : nf(d).format(Math.abs(v) < 0.5 * Math.pow(10, -d) ? 0 : v).replace(/\u00a0/g, " ");

export const signCls = (v: number | null | undefined): string => (v == null ? "" : v > 0.0000001 ? "pos" : v < -0.0000001 ? "neg" : "");

const signOf = (v: number) => (v > 0 ? "+" : v < 0 ? "−" : "");

/** Signed percentage as text, e.g. "+4,2%". */
export const pctPlain = (v: number | null | undefined, d = 1): string =>
  bad(v) || !isFinite(v) ? "–" : `${signOf(v)}${fmtNum(Math.abs(v) * 100, d)}%`;

/** Percentage text for the Pct component (plus sign for positive values only when `sign`). */
export const pctText = (v: number, d = 1, sign = true): string => `${sign && v > 0 ? "+" : v < 0 ? "−" : ""}${fmtNum(Math.abs(v) * 100, d)}%`;

export const esc = (s: unknown): string =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string);

export const fmtDate = (d: string | null | undefined): string => (d ? `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}` : "–");
export const rangeLabel = (r: { start: string; end: string }): string => `${fmtDate(r.start)} – ${fmtDate(r.end)}`;

const pad2 = (n: number) => String(n).padStart(2, "0");
export const msDay = (ms: number): string => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/** Number typed by the user: comma or dot as decimal separator, spaces ignored. */
export const parseNum = (v: unknown, def: number): number => {
  const x = parseFloat(String(v).replace(",", ".").replace(/\s/g, ""));
  return isNaN(x) ? def : x;
};

/** Value for a number input: "2,5" → "2.5" (input type=number expects a dot). */
export const numVal = (v: number, d = 1): string => fmtNum(v, d).replace(",", ".");

/** Quantity: fractional with 4 decimals, whole numbers without. */
export const fmtQty = (q: number): string => fmtNum(q, q % 1 ? 4 : 0);
/** Quantity without trailing decimal zeros (e.g. "12,5"). */
export const fmtQtyTrim = (q: number): string => fmtNum(q, 4).replace(/,?0+$/, "");

export const MONTHS = ["sty", "lut", "mar", "kwi", "maj", "cze", "lip", "sie", "wrz", "paź", "lis", "gru"];
export const monthLabel = (ym: string): string => `${MONTHS[+ym.slice(5) - 1]} ${ym.slice(2, 4)}`;

/** Heatmap cell colour (diverging scale: loss red ↔ grey ↔ gain green). */
export function heat(v: number | null | undefined, scale = 0.2): string {
  if (bad(v)) return "";
  const t = Math.max(-1, Math.min(1, v / scale));
  const a = Math.abs(t) * 0.55 + (Math.abs(t) > 0.02 ? 0.06 : 0);
  const c = t >= 0 ? "31,154,58" : "227,73,72";
  return `background:rgba(${c},${a.toFixed(3)})`;
}
